/**
 * Personal Data Validation Utilities
 * Field validation utilities based on field definitions
 */

import type { FieldDefinition } from '@xanectech/types';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - validation');

/**
 * Validation result for a single field
 */
export interface FieldValidationResult {
  is_valid: boolean;
  errors: string[];
}

/**
 * Validate a field value against its definition
 */
export function validateFieldValue(
  value: string | null | undefined,
  field: FieldDefinition,
): FieldValidationResult {
  const errors: string[] = [];

  // Check if required field is empty
  if (field.is_required && !value) {
    errors.push(
      String(field.validation_message) || `${field.field_label} is required`,
    );
    return { is_valid: false, errors };
  }

  // If value is empty and not required, it's valid
  if (!value) {
    return { is_valid: true, errors: [] };
  }

  // Length validation
  if (field.min_length && value.length < field.min_length) {
    errors.push(
      `${field.field_label} must be at least ${field.min_length} characters`,
    );
  }

  if (field.max_length && value.length > field.max_length) {
    errors.push(
      `${field.field_label} must be no more than ${field.max_length} characters`,
    );
  }

  // Numeric value validation
  if (
    field.data_type === 'number' ||
    field.data_type === 'currency' ||
    field.min_value !== null ||
    field.max_value !== null
  ) {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      errors.push(`${field.field_label} must be a valid number`);
    } else {
      if (
        field.min_value !== null &&
        field.min_value !== undefined &&
        numValue < field.min_value
      ) {
        errors.push(`${field.field_label} must be at least ${field.min_value}`);
      }
      if (
        field.max_value !== null &&
        field.max_value !== undefined &&
        numValue > field.max_value
      ) {
        errors.push(
          `${field.field_label} must be no more than ${field.max_value}`,
        );
      }
    }
  }

  // Pattern validation (regex)
  if (field.pattern) {
    try {
      const regex = new RegExp(field.pattern as string | RegExp);
      if (!regex.test(value)) {
        errors.push(
          String(field.validation_message) ||
            `${field.field_label} has an invalid format`,
        );
      }
    } catch (error) {
      logger.error('Invalid regex pattern:', {
        pattern: field.pattern,
        error: String(error),
      });
    }
  }

  // Email validation
  if (field.data_type === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      errors.push(`${field.field_label} must be a valid email address`);
    }
  }

  // URL validation
  if (field.data_type === 'url') {
    try {
      new URL(value);
    } catch {
      errors.push(`${field.field_label} must be a valid URL`);
    }
  }

  // Phone validation (basic)
  if (field.data_type === 'phone') {
    const phoneRegex = /^[+]?[\d\s()-]{10,}$/;
    if (!phoneRegex.test(value)) {
      errors.push(`${field.field_label} must be a valid phone number`);
    }
  }

  return {
    is_valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate multiple field values
 */
export function validateFieldValues(
  values: Record<string, string>,
  fields: FieldDefinition[],
): { is_valid: boolean; field_errors: Record<string, string[]> } {
  const field_errors: Record<string, string[]> = {};
  let is_valid = true;

  fields.forEach((field) => {
    const value = values[field.id];
    const result = validateFieldValue(value, field);

    if (!result.is_valid) {
      field_errors[field.id] = result.errors;
      is_valid = false;
    }
  });

  return { is_valid, field_errors };
}

/**
 * Calculate completeness percentage for an entry
 */
export function calculateCompleteness(
  values: Record<string, string>,
  fields: FieldDefinition[],
): number {
  if (fields.length === 0) return 100;

  const requiredFields = fields.filter((f) => f.is_required);
  if (requiredFields.length === 0) {
    // If no required fields, base on total fields
    const completedCount = fields.filter((f) => values[f.id]).length;
    return Math.round((completedCount / fields.length) * 100);
  }

  // Calculate based on required fields
  const completedRequiredCount = requiredFields.filter(
    (f) => values[f.id],
  ).length;
  return Math.round((completedRequiredCount / requiredFields.length) * 100);
}
